import { autoinject } from 'aurelia-dependency-injection';
import { types } from './query-proxy';
import { MessagingProxy } from './cap-proxy';
import { Remote, StoreResponse } from './shared-crm-proxy-types';
import { defaultTimeout, timeout } from './timeout';
import * as moment from 'moment';
import { RequestLicenseResponse } from '../typings/RequestLicense';
import { formatLog } from '../utils/formatter';

export type OrganizationProperties = { organizationId: string, organizationName: string, usefmnl: boolean };
export type SatelliteViewProperties = { organizationId: string, contractId: string, chartOnly: boolean, hideChartBtn: boolean };

export interface CrmSolutionInfo {
    uniquename: string;
    version: string;
    friendlyname: string;
    features: FeatureInfo[];
}

export interface FeatureInfo {
    featureCode: number;
    featureName: string;
    licenseStatus: string;
    remainingDays?: number;
    validating?: boolean;
    usage?: number;
}

export interface LicenseInput {
    featureCode: number;
    signature: string;
    expirationDate: string;
    finalExpirationDate: string;
    usageQuery: string;
    isTrial: boolean;
}
declare type Guid = string;
export type EntityKeyIndexStatus = "Active" | "Failed" | "InProgress" | "Pending";
export interface AlternateKeyStatus {

    EntityKeyIndexStatus: EntityKeyIndexStatus;
    AsyncJob: Guid;
    MetadataId: Guid;
}

export interface IErrorResponse {
    code: string;
    message: string;
}

interface IEntitySetName {
    value: string;
}

enum AsyncJobOperationType {
    EntityKeyIndexCreation = 63
}

enum AsyncJobStatusCode {
    WaitingForResources = 0,
    Waiting = 10,
    InProgress = 20,
    Pausing = 21,
    Canceling = 22,
    Succeeded = 30,
    Failed = 31,
    Canceled = 32
}
enum AsyncJobStateCode {
    Ready = 0,
    Suspended = 1,
    Locked = 2,
    Completed = 3
}

interface AsyncJob {
    asyncoperationid: Guid;
    createdon: string;
    name: string;
    primaryentitytype: string;
    statuscode: number;
    modifiedon: string;
    retrycount: number;
    startedon: string;
    data: string;
    operationtype: number;
    subtype: number;
    statecode: number;
}

@autoinject
export class OrganizationInfo {

    proxyVersion: string;

    constructor(private proxy: MessagingProxy<Remote>) {
    }

    async initialize() {
        const proxyVersion: string = await timeout(defaultTimeout,
            <Promise<string>>this.proxy.invoke("helloCRM", "v2")
        );
        this.proxyVersion = proxyVersion;
        console.log(formatLog(`Connected to CRM. Version: ${proxyVersion}`));
    }

    async retrieveAlternateKeyStatus(entityName: string, keyName: string): Promise<AlternateKeyStatus | IErrorResponse> {

        const request: types.RetrieveResourceRequest = {
            apiVersion: "v8.2",
            entitySetName: "EntityDefinitions",
            key: {
                "LogicalName": entityName
            },
            property: {
                entityName: "Keys",
                key: {
                    "LogicalName": keyName
                }
            }
        };

        const response = <AlternateKeyStatus | IErrorResponse>await timeout(defaultTimeout,
            this.proxy.invoke("retrieve", request));

        return response;
    }

    async tryRepairAlternateKey(entityName: string, prefix: string, keyName: string, currentStatus: AlternateKeyStatus) {
        if (currentStatus.EntityKeyIndexStatus === "Active") {
            console.log(formatLog("EntityKeyIndexStatus is Active and repairing is not necessary"));
            return;
        }

        if (currentStatus.AsyncJob !== "00000000-0000-0000-0000-000000000000") {
            console.log(formatLog(`Retrieving AsyncJob with Id '${currentStatus.AsyncJob}'`));
            const asyncJob = await this.retrieveAsyncJob(currentStatus.AsyncJob);
            console.log(formatLog(`AsyncJob retrieved. Id = ${currentStatus.AsyncJob}, Status = ${asyncJob.statuscode} (AsyncJobStatusCode[asyncJob.statuscode]).`));

            if (asyncJob.statuscode === AsyncJobStatusCode.InProgress || asyncJob.statuscode === AsyncJobStatusCode.WaitingForResources) {
                console.log(formatLog("Skipping InProgress or WaitingForResources AsyncJob types."));
                return;
            }

            const request = {
                apiVersion: "v8.2",
                entitySetName: "asyncoperations",
                key: currentStatus.AsyncJob
            };
            try {
                await this.proxy.invoke("delete", request);
                console.log(formatLog("AsyncJob deleted"));
            }
            catch (e) {
                console.warn(formatLog("Deleting async job failed: "), e);
            }
        }
        else {
            console.log(formatLog("No AsyncJob linked to this EntityKey"));
        }

        const alternateKeyDoubleCheck = await this.retrieveAlternateKeyStatus(entityName, keyName);

        if (!this.isErrorResponse(alternateKeyDoubleCheck) && alternateKeyDoubleCheck.EntityKeyIndexStatus === "Active") {
            //It appears that the alternate has been activated in the mean time.
            // We do not want to break things by issuing a new alternate key activation.
            console.log(formatLog("Alternate key has been activated in the mean time. Aborting this operation..."));
            return;
        }

        /** async operation request hand crafted :)
         * The content is determined by testing.
         * Correlation id is mandatory. The Guid is copied from a real request and changed a bit.
         *   It should nog be a problem when multiple asyncoperation exists with the same correlationid.
         *   It should also not happen because theoretically this record is created at most once. 
         */
        const storeRequest: types.StoreRequest2 = {
            apiVersion: "v8.2",
            entitySetName: "asyncoperations",
            data: {
                "statecode": 2,
                "correlationid": "12345678-9610-4009-bc90-e2e308b17d00",
                "depth": 1,
                "data": currentStatus.MetadataId,
                "name": `Activate AlternateKey for entity ${prefix}_license (Created by License Admin Portal)`,
                "primaryentitytype": `${prefix}_license`,
                "statuscode": <number>AsyncJobStatusCode.InProgress,
                "operationtype": <number>AsyncJobOperationType.EntityKeyIndexCreation,
                "subtype": 1
            }
        }
        const asyncOperationResult = <StoreResponse>await this.proxy.invoke("store", storeRequest);

        console.log(formatLog(`Async operation created: ${JSON.stringify(asyncOperationResult)}`));
    }

    private async retrieveAsyncJob(id: string): Promise<AsyncJob> {
        const request = {
            apiVersion: "v8.2",
            entitySetName: "asyncoperations",
            key: id
        };

        const response = <AsyncJob>await timeout(defaultTimeout,
            this.proxy.invoke("retrieve", request));

        console.log(formatLog(`OrganizationInfo.retrieveAlternateKeyStatus response: ${JSON.stringify(response)}`));

        return response;

    }

    isErrorResponse(object: any): object is IErrorResponse {
        return 'code' in object;
    }

    storeConsumptionBasedLicense(name: string, licenseInput: RequestLicenseResponse, prefix: string): Promise<any> {

        const data = {
            "statecode": 0,
            "statuscode": 1
        };

        data[`${prefix}_featurecode`] = licenseInput.featureCode;
        data[`${prefix}_expirationdate`] = null;
        data[`${prefix}_finalexpirationdate`] = null;
        data[`${prefix}_name`] = name;
        data[`${prefix}_signature`] = licenseInput.signature;
        data[`${prefix}_usagequery`] = null;
        data[`${prefix}_isconsumptionbased`] = true;
        data[`${prefix}_apikey`] = licenseInput.apiKey;
        data[`${prefix}_istrial`] = licenseInput.isTrial;

        return this.storeLicenseInternal(licenseInput.featureCode, data, prefix);
    }

    storeLicense(name: string, licenseInput: RequestLicenseResponse, prefix: string): Promise<any> {

        const data = {
            "statecode": 0,
            "statuscode": 1
        };

        data[`${prefix}_featurecode`] = licenseInput.featureCode;
        data[`${prefix}_expirationdate`] = licenseInput.expirationDate;
        data[`${prefix}_finalexpirationdate`] = licenseInput.finalExpirationDate;
        data[`${prefix}_name`] = name;
        data[`${prefix}_signature`] = licenseInput.signature;
        data[`${prefix}_usagequery`] = licenseInput.usageQuery;
        data[`${prefix}_isconsumptionbased`] = false;
        data[`${prefix}_apikey`] = null;
        data[`${prefix}_istrial`] = licenseInput.isTrial;

        return this.storeLicenseInternal(licenseInput.featureCode, data, prefix);
    }

    private async storeLicenseInternal(featureCode: number, data: any, prefix: string) {

        const storeRequest: types.StoreRequestByAlternateKeyRequest = {
            entitySetName: `${prefix}_licenses`,
            data: data,
            type: types.RetrieveRequestType.ByAlternateKey,
            apiVersion: "v8.2",
            keyname: `${prefix}_featurecode`,
            keyvalue: featureCode
        }

        return timeout(defaultTimeout,
            this.proxy.invoke("store", storeRequest)
        ).then(response => {
            if (response["error"])
                throw new Error(response["error"] && response["error"]["message"] || "Unexpected error while storing the license");

            return response;
        });
    }

    private async determineEntitySetName(query: string): Promise<string> {
        const regex = /<entity\s+name=["']([^"']+)["']/i;
        const match = query.match(regex);

        console.log(formatLog("Entityname read from query"), match);

        if (match && match[1]) {
            const entityName = match[1];
            if (entityName.indexOf("systemuser") !== -1) {
                return "systemusers";
            }

            return await this.retrieveEntitySetName(entityName);
        }

        throw new Error("Unable to determine entity set name from query");
    }

    async retrieveEntitySetName(entityName: string): Promise<string> {

        const retrieveRequest: types.RetrieveResourceRequest = {
            apiVersion: "v8.2",
            entitySetName: "EntityDefinitions",
            key: {
                "LogicalName": entityName
            },
            property: {
                entityName: "EntitySetName"
            }
        };

        const retrieveResponse = <IEntitySetName>await this.proxy.invoke("retrieve", retrieveRequest);

        return retrieveResponse.value;
    }

    async retrieveUsage(query: string): Promise<number> {

        const entitySetName = await this.determineEntitySetName(query);
        console.log(formatLog("Determined EntitySetName"), entitySetName);

        if (entitySetName === undefined) {
            // Unable to determine entitysetname, return 0 as fallback usage
            return 0;
        }

        const usageQueryFetchRequest = {
            entitySetName: entitySetName,
            fetchXml: query,
            type: types.RetrieveRequestType.ByFetchXml,
            apiVersion: "v8.2"
        } as types.FetchXmlRequest;

        return timeout(defaultTimeout,
            this.proxy.invoke("executeQuery", usageQueryFetchRequest)
        ).then(response => {
            if (response["error"])
                throw new Error(response["error"] && response["error"]["message"] || "Unexpected error while fetch users");

            return response["value"][0]["usage"];
        });
    }

    async retrieveLicensedSolutions(): Promise<CrmSolutionInfo[]> {
        var fetchXml = '<fetch distinct="false" mapping="logical">'
            + '<entity name="solution">'
            + '<attribute name="uniquename"/>'
            + '<attribute name="version"/>'
            + '<attribute name="friendlyname"/>'
            + '<link-entity name="publisher" from="publisherid" to="publisherid" link-type="inner">'
            + '<filter type="or">'
            + '<condition attribute="customizationprefix" operator="eq" value="cnm"/>'
            + '<condition attribute="customizationprefix" operator="eq" value="fmnl"/>'
            + '<condition attribute="customizationprefix" operator="eq" value="fmdk"/>'
            + '<condition attribute="customizationprefix" operator="eq" value="fmse"/>'
            + '</filter>'
            + '</link-entity>'
            + '<order attribute="friendlyname"/>'
            + '</entity>'
            + '</fetch>';
        const solutionsFetchRequest = {
            entitySetName: "solutions",
            fetchXml: fetchXml,
            type: types.RetrieveRequestType.ByFetchXml,
            apiVersion: "v8.2"
        } as types.FetchXmlRequest;

        const response = await timeout(defaultTimeout,
            this.proxy.invoke("executeQuery", solutionsFetchRequest)
        );
        if (response["error"])
            throw new Error(response["error"] && response["error"]["message"] || "Unexpected error while fetching solutions");
        return response["value"];
    }

    async retrieveLicense(featureCode: number, consumptionBasedSupported: boolean, prefix: string) {

        const columns = [
            `${prefix}_expirationdate`,
            `${prefix}_finalexpirationdate`,
            `${prefix}_featurecode`,
            `${prefix}_licenseid`,
            `${prefix}_name`,
            `${prefix}_signature`,
            `${prefix}_validationdate`,
            `${prefix}_usagequery`,
            `${prefix}_istrial`
        ];

        if (consumptionBasedSupported) {
            columns.push(`${prefix}_isconsumptionbased`);
            columns.push(`${prefix}_apikey`);
        }

        const licenseRequest = {
            entitySetName: `${prefix}_licenses`,
            columns: columns,
            keyname: `${prefix}_featurecode`,
            keyvalue: featureCode,
            type: types.RetrieveRequestType.ByAlternateKey,
            apiVersion: "v8.2"
        } as types.RetrieveByAlternateKeyRequest;

        return timeout(defaultTimeout,
            this.proxy.invoke("executeQuery", licenseRequest)
        ).then(response => {
            if (response["error"])
                throw new Error(response["error"] && response["error"]["message"] || "Unexpected error while fetching license");

            return response;
        });
    }

    determineRemainingDaysForLicense(feature: FeatureInfo, expirationDate: string) {
        let today = moment().startOf('day'); // set to 12:00 am today

        // Expiration date is retrieved as string: yyyy-MM-dd, parse to moment object
        let expDate = moment(expirationDate);

        let durationInDays = expDate.diff(today, 'days');

        console.log(formatLog(`Duration is ${durationInDays} days`));

        // When negative number of days, the trial is expired, don't show the remaining
        if (durationInDays >= 0) {
            feature.remainingDays = durationInDays;
        }
    }
}


// WEBPACK FOOTER //
// ./ClientApp/app/services/organization-info.ts